import { useRef, useState, useEffect } from 'react';
import type { RefObject } from 'react';

export type UseScrollTriggerSetter = (value: boolean) => void;

export interface UseScrollTriggerOptions {
  /** Scroll behavior */
  behavior?: ScrollBehavior;
  /** Block alignment */
  block?: ScrollLogicalPosition;
}

export interface UseScrollTriggerReturn<T extends Element> {
  /** Ref to attach to the scroll marker element */
  ref: RefObject<T | null>;
  /** Function to trigger scrolling to the marker element */
  trigger: UseScrollTriggerSetter;
}

/**
 * useScrollTrigger
 *
 * Hook that exposes a ref and a trigger function.
 * When the trigger is called with `true`, the element will be scrolled into view once.
 * Use CSS `scroll-margin-top` on the target element to account for sticky headers.
 *
 * @example
 * const { ref, trigger } = useScrollTrigger<HTMLDivElement>();
 * // In JSX: <div ref={ref} style={{ scrollMarginTop: '100px' }} />
 * trigger(true);
 */
export const useScrollTrigger = <T extends Element>(
  options: UseScrollTriggerOptions = {},
): UseScrollTriggerReturn<T> => {
  const { behavior = 'smooth', block = 'start' } = options;
  const ref = useRef<T | null>(null);
  const [shouldScrollTo, setShouldScrollTo] = useState(false);

  useEffect(() => {
    if (ref.current && shouldScrollTo) {
      ref.current.scrollIntoView({ behavior, block });
      setShouldScrollTo(false);
    }
  }, [shouldScrollTo, behavior, block]);

  return { ref, trigger: setShouldScrollTo };
};

export default useScrollTrigger;
